import {createTheme, type ThemeOptions} from '@mui/material/styles';

import {MUI_LAYER_THEME} from '../../../../../../util/layers';
import {foundations} from '../../foundation';
import {MuiButton} from './components/MuiButton';
import {MuiChip} from './components/MuiChip';
import {MuiInputBase} from './components/MuiInputBase';
import {MuiInputLabel} from './components/MuiInputLabel';
import {palette} from './palette';

const {colors, spacing, fontWeights} = foundations;

const lightThemeOptions: ThemeOptions = {
  spacing,
  palette,
  typography: {
    fontFamily: "'Source Sans 3'",
    button: {
      fontFamily: "'Source Sans 3'",
      textTransform: 'none',
      letterSpacing: 'none',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: MuiButton,
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {fontSize: spacing(3)},
        arrow: {width: spacing(4)},
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
    },
    MuiInputLabel: {
      defaultProps: {
        disableAnimation: true,
        shrink: false,
      },
      styleOverrides: MuiInputLabel,
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        input: {
          padding: spacing(3, 2),
        },
        sizeSmall: {
          '& .MuiOutlinedInput-input': {
            paddingTop: spacing(2),
            paddingBottom: spacing(2),
          },
        },
        adornedStart: {
          paddingLeft: spacing(2),
        },
        adornedEnd: {
          paddingRight: spacing(2),
        },
        multiline: {
          padding: spacing(3, 2),
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderBottomLeftRadius: spacing(1),
          borderBottomRightRadius: spacing(1),
          backgroundColor: colors.blackAlpha[50],
          '&:hover': {
            backgroundColor: colors.blackAlpha[200],
          },
        },
        input: {
          padding: spacing(3, 2),
        },
        sizeSmall: {
          '& .MuiFilledInput-input': {
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        },
        adornedStart: {
          paddingLeft: '8px',
        },
        adornedEnd: {
          paddingRight: '8px',
        },
        multiline: {
          padding: '10px 8px',
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 3,
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
        },
      },
    },
    MuiIcon: {
      defaultProps: {
        component: 'i',
      } as any,
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.gray[800],
          padding: spacing(2),
          borderRadius: spacing(1),
          '&:focus': {
            backgroundColor: colors.blackAlpha[50],
          },
          '&:disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        },
        colorPrimary: {
          color: foundations.legacy.primary,
          '&:hover': {
            backgroundColor: 'rgba(46, 120, 199, 0.04)',
          },
          '&:focus': {
            backgroundColor: 'rgba(46, 120, 199, 0.04)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: MuiChip,
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: fontWeights.semibold,
          letterSpacing: foundations.letterSpacings.normal,
          color: colors.gray[800],
          '&.Mui-focused': {
            color: colors.gray[800],
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: MuiInputBase,
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: {
          marginRight: '4px',
        },
        positionEnd: {
          marginLeft: '0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '0',
          paddingBottom: '0',
          lineHeight: spacing(10),
        },
        dense: {
          paddingTop: '0',
          paddingBottom: '0',
          lineHeight: spacing(8),
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          lineHeight: '2.5rem',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          paddingRight: '8px',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: spacing(10),
          color: 'inherit',
        },
      },
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          right: spacing(2),
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: '7px',
        },
        thumb: {
          boxShadow:
            '0px 0px 1px -1px rgb(0 0 0 / 20%), 0px 0px 1px 0px rgb(0 0 0 / 20%), 0px 0px 3px 0px rgb(0 0 0 / 12%)',
        },
        switchBase: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        track: {
          borderRadius: '25px',
        },
        sizeSmall: {
          padding: '2px',
        },
      },
    },
  },
};

// MUI_LAYER_THEME is deep-merged last so MUI overlays sit on the
// @wandb/components layer scale (see util/layers.ts). DarkTheme derives from
// this theme and inherits it.
export const LightTheme = createTheme(lightThemeOptions, MUI_LAYER_THEME);
